import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import Btn from './ui/Btn';
import ImportJowZipButton from './ImportJowZipButton';
import MigratePieceDialog from './MigratePieceDialog';
import { ApiError, api } from '../lib/api';
import { confirmAndDeletePdf } from '../lib/pdfActions';
import { toast } from '../stores/toaster';
import type { InventorySourcePreference } from '../lib/sortPieces';
import { InventaireEntry, PdfMetadata } from '../lib/types';

/**
 * Onglet « Inventaire greffe » du hub dossier (M3 / Sprint 6 / ADR-046).
 *
 * Saisie manuelle des entrées d'inventaire officielles
 * (Carton · sous-farde · pièce). L'extraction IA depuis un PDF
 * d'inventaire viendra Sprint 6-2 ; pour l'instant l'avocat saisit
 * directement la cotation reçue du greffe.
 *
 * Liaison PDF ↔ entry : la liste des pièces (DossierDetailPage)
 * surface le sélecteur ; ce composant montre seulement combien de
 * pièces sont liées à chaque entry.
 */

const TYPE_SUGGESTIONS = [
  'Audition',
  'PV',
  'Apostille',
  'Réquisitoire',
  'Mandat',
  'Procès-verbal de constat',
  'Expertise',
  'Plainte',
  'Audition libre',
  'Garde à vue',
  'Perquisition',
  'Conclusions',
  'Jugement',
  'Arrêt',
  'Autre',
];

type DraftForm = {
  carton: string;
  sous_farde: string;
  piece: string;
  titre_piece: string;
  type_piece: string;
  date_piece: string;
  nombre_pages: string;
};

const EMPTY_DRAFT: DraftForm = {
  carton: '',
  sous_farde: '',
  piece: '',
  titre_piece: '',
  type_piece: '',
  date_piece: '',
  nombre_pages: '',
};

type Props = {
  dossierId: string;
  pdfs: PdfMetadata[];
  onCountChange?: (count: number) => void;
  onChange?: () => void;
};

function cotationLong(e: InventaireEntry): string {
  const parts: string[] = [];
  if (e.carton) parts.push(`Carton ${e.carton}`);
  if (e.sous_farde) parts.push(`farde ${e.sous_farde}`);
  if (e.piece) parts.push(`pièce ${e.piece}`);
  return parts.length > 0 ? parts.join(' · ') : '— sans cotation —';
}

export default function InventaireTab({ dossierId, pdfs, onCountChange, onChange }: Props) {
  const [entries, setEntries] = useState<InventaireEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftForm>(EMPTY_DRAFT);
  // Sprint 2 §10 — import IA d'un inventaire greffe (PDF/DOCX).
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  // Toggle vue : 'manual' = liste tabulaire des entrées · 'auto' = arbre
  // par type IA. Default 'auto' si pas d'entrées, sinon 'manual'.
  // Le user peut basculer manuellement (la vue auto reste utile même
  // après saisie d'entrées : moins dense, regroupée par type).
  const [viewMode, setViewMode] = useState<'manual' | 'auto'>('manual');
  const [viewModeManuallySet, setViewModeManuallySet] = useState(false);

  // ADR-039 — préférence d'ordre des pièces basée sur l'inventaire
  // (auto = toutes les entrées · manual = entrées saisies à la main ·
  // ai_parsed = entrées extraites par IA). Pilote l'ordre dans la
  // liste des pièces + le viewer. Persisté en localStorage.
  const [sourcePref, setSourcePrefState] = useState<InventorySourcePreference>(() => {
    const s = localStorage.getItem(`adr_inv_pref_${dossierId}`);
    return s === 'manual' || s === 'ai_parsed' || s === 'auto' ? s : 'auto';
  });
  const setSourcePref = (pref: InventorySourcePreference) => {
    setSourcePrefState(pref);
    localStorage.setItem(`adr_inv_pref_${dossierId}`, pref);
  };

  const triggerImport = () => {
    if (importing) return;
    importInputRef.current?.click();
  };

  const onImportFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset pour permettre de re-uploader le meme fichier
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.postForm<{
        created: number;
        skipped: number;
        entries: InventaireEntry[];
      }>(`/dossiers/${dossierId}/inventaire/import`, fd);
      if (resp.created === 0) {
        toast.info('Aucune entrée détectée — vérifie le format de ton inventaire.');
      } else {
        toast.success(
          `${resp.created} entrée${resp.created > 1 ? 's' : ''} importée${resp.created > 1 ? 's' : ''}` +
            (resp.skipped > 0 ? ` (${resp.skipped} ignorée${resp.skipped > 1 ? 's' : ''})` : '') +
            ' — à vérifier'
        );
      }
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.get<InventaireEntry[]>(`/dossiers/${dossierId}/inventaire`);
      setEntries(rows);
      onCountChange?.(rows.length);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  // Default view mode : 'auto' si pas d'entrées, 'manual' sinon.
  // Une fois que l'utilisateur a basculé manuellement, on respecte son
  // choix (pas de bascule auto pendant cette session).
  useEffect(() => {
    if (viewModeManuallySet) return;
    setViewMode(entries.length === 0 ? 'auto' : 'manual');
  }, [entries.length, viewModeManuallySet]);

  // Per-entry: how many PDF pieces are linked? Helps Maxim see at a
  // glance whether the cotation is « occupée » par un PDF déjà
  // chargé ou attend toujours sa pièce.
  const linkedPdfCountByEntry = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pdfs) {
      if (p.inventaire_entry_id) {
        m.set(p.inventaire_entry_id, (m.get(p.inventaire_entry_id) ?? 0) + 1);
      }
    }
    return m;
  }, [pdfs]);

  const buildPayload = (d: DraftForm): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    if (d.carton.trim()) out.carton = d.carton.trim();
    if (d.sous_farde.trim()) out.sous_farde = d.sous_farde.trim();
    if (d.piece.trim()) out.piece = d.piece.trim();
    if (d.titre_piece.trim()) out.titre_piece = d.titre_piece.trim();
    if (d.type_piece.trim()) out.type_piece = d.type_piece.trim();
    if (d.date_piece) out.date_piece = d.date_piece;
    if (d.nombre_pages.trim()) {
      const n = parseInt(d.nombre_pages, 10);
      if (Number.isFinite(n)) out.nombre_pages = n;
    }
    return out;
  };

  const submitNew = async () => {
    setSubmitting(true);
    try {
      await api.post<InventaireEntry>(`/dossiers/${dossierId}/inventaire`, buildPayload(draft));
      setAdding(false);
      setDraft(EMPTY_DRAFT);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (e: InventaireEntry) => {
    setEditingId(e.id);
    setEditDraft({
      carton: e.carton ?? '',
      sous_farde: e.sous_farde ?? '',
      piece: e.piece ?? '',
      titre_piece: e.titre_piece ?? '',
      type_piece: e.type_piece ?? '',
      date_piece: e.date_piece ?? '',
      nombre_pages: e.nombre_pages != null ? String(e.nombre_pages) : '',
    });
  };

  const submitEdit = async () => {
    if (!editingId) return;
    setSubmitting(true);
    try {
      // For PATCH we explicitly send nullable fields too (so cleared
      // input → null on the server). Skip empty + falsy nombre_pages.
      const body: Record<string, unknown> = {
        carton: editDraft.carton.trim() || null,
        sous_farde: editDraft.sous_farde.trim() || null,
        piece: editDraft.piece.trim() || null,
        titre_piece: editDraft.titre_piece.trim() || null,
        type_piece: editDraft.type_piece.trim() || null,
        date_piece: editDraft.date_piece || null,
      };
      if (editDraft.nombre_pages.trim()) {
        const n = parseInt(editDraft.nombre_pages, 10);
        if (Number.isFinite(n)) body.nombre_pages = n;
      } else {
        body.nombre_pages = null;
      }
      await api.patch<InventaireEntry>(`/inventaire/${editingId}`, body);
      setEditingId(null);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (e: InventaireEntry) => {
    if (!window.confirm(`Supprimer ${cotationLong(e)} ?`)) return;
    try {
      await api.delete(`/inventaire/${e.id}`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper-elevated p-3">
        <div className="space-y-1">
          <div className="text-[12px] text-text-secondary">
            <strong className="font-medium text-text-primary">{entries.length}</strong> entrée
            {entries.length > 1 ? 's' : ''}
            {pdfs.length > 0 && (
              <>
                {' · '}
                <strong className="font-medium text-text-primary">
                  {pdfs.filter((p) => p.inventaire_entry_id).length}
                </strong>
                /{pdfs.length} pièce{pdfs.length > 1 ? 's' : ''} cotée
                {pdfs.filter((p) => p.inventaire_entry_id).length > 1 ? 's' : ''}
              </>
            )}
          </div>
          {entries.length > 0 && (
            <label className="flex items-center gap-2 text-[11px] text-text-tertiary">
              <span>Ordre des pièces (lecture + viewer) :</span>
              <select
                value={sourcePref}
                onChange={(e) => setSourcePref(e.target.value as InventorySourcePreference)}
                className="rounded border border-rule-strong bg-paper-elevated px-1.5 py-0.5 text-[11px]"
                title="Pilote l'ordre des pièces dans la liste du dossier et dans le viewer (flèches précédent/suivant)"
              >
                <option value="auto">Inventaire (auto, toutes sources)</option>
                <option value="manual">Inventaire saisi manuellement</option>
                <option value="ai_parsed">Inventaire extrait par IA</option>
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pdfs.length > 0 && (
            <div
              className="inline-flex overflow-hidden rounded-md border border-rule-strong"
              role="tablist"
              aria-label="Mode d'affichage de l'inventaire"
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'manual'}
                onClick={() => {
                  setViewMode('manual');
                  setViewModeManuallySet(true);
                }}
                className={`px-3 py-1 text-[12px] transition ${
                  viewMode === 'manual'
                    ? 'bg-ink text-paper-elevated'
                    : 'bg-paper-card text-text-secondary hover:text-text-primary'
                }`}
                title="Liste des entrées d'inventaire saisies (manuelles ou IA)"
              >
                Tabulaire
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'auto'}
                onClick={() => {
                  setViewMode('auto');
                  setViewModeManuallySet(true);
                }}
                className={`px-3 py-1 text-[12px] transition ${
                  viewMode === 'auto'
                    ? 'bg-accent text-paper-elevated'
                    : 'bg-paper-card text-text-secondary hover:text-text-primary'
                }`}
                title="Vue auto : pièces regroupées par type (PV, Audition, Apostille…) générée par IA"
              >
                ✨ Vue IA
              </button>
            </div>
          )}
          <input
            ref={importInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xlsx,.xls,.ods,.msg,.eml,.jpg,.jpeg,.png,.tiff"
            onChange={(e) => void onImportFileChosen(e)}
            className="hidden"
          />
          <Btn
            variant="secondary"
            size="sm"
            onClick={triggerImport}
            disabled={importing}
            title="Importe un inventaire greffe existant (PDF ou Word) parsé par IA"
          >
            {importing ? 'Import IA…' : '↥ Importer un inventaire'}
          </Btn>
          <ImportJowZipButton dossierId={dossierId} onDone={() => void load()} />
          {!adding && viewMode === 'manual' && (
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_DRAFT);
                setAdding(true);
              }}
            >
              + Ajouter une entrée
            </Btn>
          )}
        </div>
      </div>

      {adding && (
        <EntryForm
          draft={draft}
          setDraft={setDraft}
          onCancel={() => {
            setAdding(false);
            setDraft(EMPTY_DRAFT);
          }}
          onSubmit={() => void submitNew()}
          submitting={submitting}
          submitLabel="Créer l'entrée"
        />
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Chargement…</p>
      ) : viewMode === 'auto' && pdfs.length > 0 ? (
        <AutoInventoryView pdfs={pdfs} dossierId={dossierId} onChange={onChange} />
      ) : entries.length === 0 && !adding ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucune entrée d&apos;inventaire saisie. Bascule sur ✨ Vue IA pour voir les pièces
          regroupées par type, ou utilise « + Ajouter une entrée » pour saisir manuellement.
        </p>
      ) : (
        <ul className="space-y-2">
          {entries.map((e) =>
            editingId === e.id ? (
              <li key={e.id}>
                <EntryForm
                  draft={editDraft}
                  setDraft={setEditDraft}
                  onCancel={() => setEditingId(null)}
                  onSubmit={() => void submitEdit()}
                  submitting={submitting}
                  submitLabel="Enregistrer"
                />
              </li>
            ) : (
              <li key={e.id}>
                <EntryRow
                  entry={e}
                  linkedPdfs={linkedPdfCountByEntry.get(e.id) ?? 0}
                  onEdit={() => startEdit(e)}
                  onDelete={() => void remove(e)}
                />
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Vue auto quand aucun inventaire greffe n'a été saisi : organise les PDFs
 * en 2 sections — « Procès-verbaux » triés par numéro extrait du filename,
 * « Autres pièces » triées par date_pv si dispo, sinon par uploaded_at.
 *
 * Heuristiques de détection PV :
 *   - filename match /\bPV[\s.\-_]*(\d+)([\/\-](\d+))?/i
 *   - ou commence par « audition », « interrogatoire », « constat »
 */
type TreeItem = {
  pdf_file_id: string;
  filename_original: string;
  page_count: number | null;
  type: string;
  numero_pv: string | null;
  date_piece: string | null;
  confidence: number | null;
  classification_source: 'ai' | 'heuristic' | null;
  uploaded_at: string | null;
  review_status?: string;
};

type TreeGroup = { type: string; label: string; count: number; items: TreeItem[] };
type TreeResponse = { groups: TreeGroup[]; total: number };

function AutoInventoryView({
  pdfs,
  dossierId,
  onChange,
}: {
  pdfs: PdfMetadata[];
  dossierId: string;
  onChange: (() => void) | undefined;
}) {
  // Arbre IA chargé depuis /tree. Si une classification existe pour ≥1 PDF
  // on l'affiche en priorité ; sinon on retombe sur l'heuristique filename.
  const [tree, setTree] = useState<TreeResponse | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [migratePdf, setMigratePdf] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    api
      .get<TreeResponse>(`/dossiers/${dossierId}/tree`)
      .then(setTree)
      .catch(() => setTree(null));
  }, [dossierId, pdfs.length]);

  const aiClassifiedCount = useMemo(
    () =>
      tree?.groups.reduce(
        (sum, g) => sum + g.items.filter((i) => i.classification_source !== null).length,
        0
      ) ?? 0,
    [tree]
  );

  const triggerReclassify = async () => {
    setClassifying(true);
    try {
      await api.post(`/dossiers/${dossierId}/tree/regenerate`, { force: false });
      toast.success(
        'Classification IA en cours… Recharge la page dans 1-2 min pour voir les résultats.'
      );
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setClassifying(false);
    }
  };

  type PvInfo = { num: number; year: number | null; raw: string };

  const detectPv = (filename: string): PvInfo | null => {
    const m = /\bPV[\s.\-_]*(\d+)(?:[/-](\d{2,4}))?/i.exec(filename);
    if (m) {
      return {
        num: parseInt(m[1]!, 10),
        year: m[2] ? parseInt(m[2], 10) : null,
        raw: m[0],
      };
    }
    if (/^(audition|interrogatoire|constat)/i.test(filename.trim())) {
      // Pas de numéro → on retourne 0 pour qu'il aille en fin de la section PV.
      return { num: Number.MAX_SAFE_INTEGER, year: null, raw: 'sans n°' };
    }
    return null;
  };

  const { pvs, others } = useMemo(() => {
    const pvList: { p: PdfMetadata; pv: PvInfo }[] = [];
    const otherList: PdfMetadata[] = [];
    for (const p of pdfs) {
      const pv = detectPv(p.filename_original);
      if (pv) pvList.push({ p, pv });
      else otherList.push(p);
    }
    pvList.sort((a, b) => {
      // Tri par année, puis numéro.
      const ya = a.pv.year ?? -Infinity;
      const yb = b.pv.year ?? -Infinity;
      if (ya !== yb) return ya - yb;
      return a.pv.num - b.pv.num;
    });
    otherList.sort((a, b) => {
      const da = a.date_pv ?? a.uploaded_at;
      const db = b.date_pv ?? b.uploaded_at;
      return da.localeCompare(db);
    });
    return { pvs: pvList, others: otherList };
  }, [pdfs]);

  if (pdfs.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
        Aucune pièce dans le dossier. Importe d'abord des PDFs dans Pièces.
      </p>
    );
  }

  const renamePdf = async (pdfId: string, currentName: string) => {
    const next = window.prompt('Nouveau nom de la pièce', currentName);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      await api.patch<PdfMetadata>(`/pdfs/${pdfId}`, { filename_original: trimmed });
      toast.success('Pièce renommée');
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const removePdf = async (pdfId: string, currentName: string) => {
    if (await confirmAndDeletePdf(pdfId, currentName)) {
      onChange?.();
    }
  };

  const Row = ({ p, hint }: { p: PdfMetadata; hint?: string }) => (
    <li>
      <Link
        to={`/dossiers/${dossierId}/pieces/${p.id}`}
        className="group flex items-center justify-between gap-3 rounded-sm border border-rule bg-paper-elevated px-3 py-2 text-[13px] text-text-primary no-underline hover:bg-paper-card"
        title={p.filename_original}
      >
        <span className="min-w-0 flex-1 truncate">
          {hint && <span className="mr-1.5 font-mono text-[11px] text-text-tertiary">{hint}</span>}
          {p.filename_original}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void renamePdf(p.id, p.filename_original);
          }}
          className="shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-paper-subtle hover:text-text-primary"
          title="Renommer la pièce"
        >
          ✏
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMigratePdf({ id: p.id, title: p.filename_original });
          }}
          className="shrink-0 rounded border border-accent/30 bg-accent-light px-1.5 py-0.5 text-[11px] text-accent-text transition hover:bg-accent hover:text-paper-elevated"
          title="Migrer vers un autre bucket"
        >
          ↗ Ranger
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void removePdf(p.id, p.filename_original);
          }}
          className="shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
          title="Supprimer la pièce"
        >
          🗑
        </button>
        <span className="shrink-0 text-[11px] text-text-tertiary">
          {p.page_count ? `${p.page_count} p.` : '—'}
        </span>
      </Link>
    </li>
  );

  // Si l'arbre IA est dispo et a classifié au moins quelques pièces,
  // on l'affiche en priorité (groupé par type).
  const useAiTree = tree && aiClassifiedCount > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-accent/40 bg-accent-lighter px-4 py-3 text-[12px] text-accent-text">
        <span>
          <strong>Aucun inventaire greffe saisi.</strong>{' '}
          {useAiTree
            ? `Vue auto par type — ${aiClassifiedCount}/${pdfs.length} pièce(s) classifiée(s) par IA.`
            : 'Vue auto par filename — PV triés par numéro, autres par date.'}
        </span>
        <Btn
          variant="secondary"
          size="sm"
          onClick={() => void triggerReclassify()}
          disabled={classifying}
          title="Lance une (re)classification IA des pièces non encore classées"
        >
          {classifying ? 'Lancé…' : '↯ Classifier IA'}
        </Btn>
      </div>

      {useAiTree ? (
        <>
          {tree!.groups.map((g) => (
            <section key={g.type}>
              <h3 className="mb-2 font-serif text-[15px] font-medium text-text-primary">
                {g.label} ({g.count})
              </h3>
              <ul className="space-y-1.5">
                {g.items.map((it) => {
                  const pdfMeta = pdfs.find((p) => p.id === it.pdf_file_id);
                  if (!pdfMeta) return null;
                  const hint =
                    it.type === 'pv' && it.numero_pv
                      ? `n° ${it.numero_pv}`
                      : it.date_piece
                        ? new Date(it.date_piece).toLocaleDateString('fr-BE')
                        : '';
                  return (
                    <Row
                      key={it.pdf_file_id}
                      p={pdfMeta}
                      hint={
                        hint +
                        (it.classification_source === 'heuristic' ? ' · ⚙' : '') +
                        (it.classification_source === 'ai' ? ' · 🤖' : '')
                      }
                    />
                  );
                })}
              </ul>
            </section>
          ))}
        </>
      ) : (
        <>
          {pvs.length > 0 && (
            <section>
              <h3 className="mb-2 font-serif text-[15px] font-medium text-text-primary">
                📄 Procès-verbaux ({pvs.length})
              </h3>
              <ul className="space-y-1.5">
                {pvs.map(({ p, pv }) => (
                  <Row
                    key={p.id}
                    p={p}
                    hint={
                      pv.num === Number.MAX_SAFE_INTEGER
                        ? '—'
                        : `n° ${pv.num}${pv.year ? `/${pv.year}` : ''}`
                    }
                  />
                ))}
              </ul>
            </section>
          )}

          {others.length > 0 && (
            <section>
              <h3 className="mb-2 font-serif text-[15px] font-medium text-text-primary">
                📁 Autres pièces ({others.length})
              </h3>
              <ul className="space-y-1.5">
                {others.map((p) => (
                  <Row
                    key={p.id}
                    p={p}
                    hint={
                      p.date_pv
                        ? new Date(p.date_pv).toLocaleDateString('fr-BE')
                        : new Date(p.uploaded_at).toLocaleDateString('fr-BE')
                    }
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {migratePdf && (
        <MigratePieceDialog
          pdfId={migratePdf.id}
          pdfTitle={migratePdf.title}
          onClose={() => setMigratePdf(null)}
          onMigrated={() => {
            setMigratePdf(null);
            onChange?.();
          }}
        />
      )}
    </div>
  );
}

function EntryRow({
  entry: e,
  linkedPdfs,
  onEdit,
  onDelete,
}: {
  entry: InventaireEntry;
  linkedPdfs: number;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex items-start justify-between gap-3 rounded-sm border border-rule bg-paper-elevated p-3.5 hover:bg-paper-card">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-[12px] font-medium tracking-[0.3px] text-text-primary">
            {cotationLong(e)}
          </span>
          {e.type_piece && (
            <span className="inline-flex items-center rounded-pill bg-paper-subtle px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-text-secondary">
              {e.type_piece}
            </span>
          )}
          {e.source === 'ai_parsed' && (
            <span
              className="inline-flex items-center rounded-pill bg-cat-imp-pastel px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-cat-imp-text"
              title="Extrait par IA — à vérifier"
            >
              à vérifier
            </span>
          )}
        </div>
        {e.titre_piece && (
          <p className="mt-1 font-serif text-[14px] text-text-primary">{e.titre_piece}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
          {e.date_piece && (
            <span>
              <span className="font-mono">{e.date_piece}</span>
            </span>
          )}
          {e.nombre_pages != null && <span className="font-mono">{e.nombre_pages} p.</span>}
          {linkedPdfs > 0 && (
            <span className="rounded-pill bg-cat-fav-pastel px-2 py-[2px] font-medium text-cat-fav">
              {linkedPdfs} pièce{linkedPdfs > 1 ? 's' : ''} PDF lié{linkedPdfs > 1 ? 'es' : 'e'}
            </span>
          )}
          {linkedPdfs === 0 && <span className="italic">aucune pièce PDF liée</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
        <button
          onClick={onEdit}
          className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
          title="Modifier"
        >
          ✎
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
          title="Supprimer"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function EntryForm({
  draft,
  setDraft,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  draft: DraftForm;
  setDraft: (d: DraftForm) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const labelCls = 'block text-xs font-medium uppercase tracking-[0.3px] text-text-secondary';
  const inputCls =
    'mt-1 w-full rounded-sm border border-rule-strong bg-paper-elevated px-2.5 py-1.5 text-sm font-normal normal-case tracking-normal text-text-primary';
  return (
    <div className="space-y-3 rounded-md border border-accent/40 bg-accent-lighter p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className={labelCls}>
          Carton
          <input
            value={draft.carton}
            onChange={(e) => setDraft({ ...draft, carton: e.target.value })}
            placeholder="ex. IX"
            autoFocus
            className={`${inputCls} font-mono`}
          />
        </label>
        <label className={labelCls}>
          Sous-farde
          <input
            value={draft.sous_farde}
            onChange={(e) => setDraft({ ...draft, sous_farde: e.target.value })}
            placeholder="ex. B"
            className={`${inputCls} font-mono`}
          />
        </label>
        <label className={labelCls}>
          Pièce
          <input
            value={draft.piece}
            onChange={(e) => setDraft({ ...draft, piece: e.target.value })}
            placeholder="ex. 14"
            className={`${inputCls} font-mono`}
          />
        </label>
      </div>
      <label className={labelCls}>
        Titre / objet de la pièce
        <input
          value={draft.titre_piece}
          onChange={(e) => setDraft({ ...draft, titre_piece: e.target.value })}
          placeholder="ex. Audition X. Martin"
          className={inputCls}
        />
      </label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className={labelCls}>
          Type
          <input
            value={draft.type_piece}
            onChange={(e) => setDraft({ ...draft, type_piece: e.target.value })}
            list="inventaire-type-suggestions"
            placeholder="Audition, PV…"
            className={inputCls}
          />
          <datalist id="inventaire-type-suggestions">
            {TYPE_SUGGESTIONS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        <label className={labelCls}>
          Date
          <input
            type="date"
            value={draft.date_piece}
            onChange={(e) => setDraft({ ...draft, date_piece: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className={labelCls}>
          Nombre de pages
          <input
            type="number"
            min={0}
            max={99999}
            value={draft.nombre_pages}
            onChange={(e) => setDraft({ ...draft, nombre_pages: e.target.value })}
            className={inputCls}
          />
        </label>
      </div>
      <div className="flex justify-end gap-1.5">
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Annuler
        </Btn>
        <Btn variant="primary" size="sm" onClick={onSubmit} disabled={submitting}>
          {submitting ? '…' : submitLabel}
        </Btn>
      </div>
    </div>
  );
}
